import { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";
import { useAlarmColor } from "../../../../../../../../hooks/use-alarm-color";
import { ITrainControlsResponse } from "../../interfaces";
import { useBlinkAnimation } from "../use-blink-animation";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";

interface ITiLinearGaugeProps {
  control: ITrainControlsResponse;
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

function parseNumericValue(raw: string | null): number {
  if (raw == null || raw === "") return 0;
  const n = parseFloat(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function yFromFraction(f: number, stemTop: number, stemBottom: number): number {
  return stemBottom - f * (stemBottom - stemTop);
}

function getScaleFallbackDigits(min: number, max: number): number {
  const span = max - min;
  if (span >= 20) return 0;
  if (span >= 2) return 1;
  return 2;
}

const VIEW_W = 132;
const VIEW_H = 198;
const STEM_TOP = 14;
const STEM_BOTTOM = 176;
const SPINE_X = 40;
const TICK_MAJOR_IN = 16;
const TICK_MINOR_IN = 9;
const LABEL_X = 46;
const POINTER_W = 15;
const POINTER_H = 12;
const SCALE_FONT = 13;

export const TiLinearGauge: FC<ITiLinearGaugeProps> = ({ control, flashUnacknowledgedAlarms, flashFaultAlarms }) => {
  const { value, props, alarmStatus, unacknowledgedEvent } = control;
  const p = props as Record<string, unknown>;
  const { profile } = useUser<IUser>();

  const minRaw = p.MinValue ?? p.Min ?? p.mMin;
  const maxRaw = p.MaxValue ?? p.Max ?? p.mMax;
  const min = typeof minRaw === "number" && Number.isFinite(minRaw) ? minRaw : 0;
  const max = typeof maxRaw === "number" && Number.isFinite(maxRaw) && maxRaw > min ? maxRaw : 100;

  const numeric = useMemo(() => parseNumericValue(value), [value]);
  const valueFrac = useMemo(() => {
    if (max <= min) return 0;
    return Math.min(1, Math.max(0, (numeric - min) / (max - min)));
  }, [numeric, min, max]);

  const majorFracs = useMemo(() => [0, 0.25, 0.5, 0.75, 1] as const, []);
  const scaleFallbackDigits = useMemo(() => getScaleFallbackDigits(min, max), [min, max]);
  const minorFracs = useMemo(() => {
    const out: number[] = [];
    for (let i = 1; i < 20; i++) {
      if (i % 5 === 0) continue;
      out.push(i / 20);
    }
    return out;
  }, []);

  const alarmTint = useAlarmColor(Number(alarmStatus) || 0);
  const pointerColor = alarmStatus > 0 ? alarmTint : "#1976d2";

  const shouldBlinkBg = flashUnacknowledgedAlarms && unacknowledgedEvent > 0;
  const shouldBlinkBorder = flashFaultAlarms && unacknowledgedEvent === 2;
  const { animation, keyframes } = useBlinkAnimation(shouldBlinkBg, shouldBlinkBorder, alarmTint);

  const w = Number(props.Width) || 100;
  const h = Number(props.Height) || 100;
  const showText = w >= 56 && h >= 72;

  const yPointer = yFromFraction(valueFrac, STEM_TOP, STEM_BOTTOM);
  const shadowId = `lg-dropshadow-${control.idSchemeObject}`;

  const pointerPts = `${SPINE_X},${yPointer} ${SPINE_X - POINTER_W},${yPointer - POINTER_H / 2} ${
    SPINE_X - POINTER_W
  },${yPointer + POINTER_H / 2}`;

  return (
    <Box
      sx={{
        position: "absolute",
        left: props.Left,
        top: props.Top,
        width: props.Width,
        height: props.Height,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        justifyContent: "flex-start",
        boxSizing: "border-box",
        border: "1px solid #37474f",
        borderRadius: "4px",
        backgroundColor: "rgba(255,255,255,0.92)",
        overflow: "hidden",
        p: "2px",
        gap: showText ? "2px" : 0,
        minWidth: 0,
        minHeight: 0,
        animation,
        ...keyframes,
      }}
    >
      <Box
        sx={{
          position: "relative",
          flex: "1 1 0",
          minHeight: 0,
          minWidth: 0,
          width: 0,
          height: "100%",
          alignSelf: "stretch",
        }}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMinYMax meet"
          shapeRendering="geometricPrecision"
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            display: "block",
          }}
          aria-hidden
        >
          <defs>
            <filter id={shadowId} x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="1" dy="2" stdDeviation="1.2" floodColor="#000000" floodOpacity="0.35" />
            </filter>
          </defs>

          <line
            x1={SPINE_X}
            y1={STEM_TOP}
            x2={SPINE_X}
            y2={STEM_BOTTOM}
            stroke="#000000"
            strokeWidth={1.25}
            strokeLinecap="square"
          />

          {minorFracs.map((f, i) => {
            const y = yFromFraction(f, STEM_TOP, STEM_BOTTOM);
            return (
              <line
                key={`m-${i}`}
                x1={SPINE_X}
                y1={y}
                x2={SPINE_X - TICK_MINOR_IN}
                y2={y}
                stroke="#424242"
                strokeWidth={1}
                strokeLinecap="butt"
              />
            );
          })}

          {majorFracs.map((f, i) => {
            const y = yFromFraction(f, STEM_TOP, STEM_BOTTOM);
            const labelVal = min + f * (max - min);
            return (
              <g key={`M-${i}`}>
                <line
                  x1={SPINE_X}
                  y1={y}
                  x2={SPINE_X - TICK_MAJOR_IN}
                  y2={y}
                  stroke="#000000"
                  strokeWidth={1}
                  strokeLinecap="butt"
                />
                <text
                  x={LABEL_X}
                  y={y}
                  fill="#000000"
                  fontSize={SCALE_FONT}
                  fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
                  fontWeight={700}
                  dominantBaseline="middle"
                  textAnchor="start"
                  style={{ userSelect: "none" }}
                >
                  {formatMeasurementNumber(labelVal, control.valueUnits ?? null, profile.interfaceSettings.dataFormat, {
                    fallbackDigits: scaleFallbackDigits,
                  })}
                </text>
              </g>
            );
          })}

          <polygon points={pointerPts} fill={pointerColor} filter={`url(#${shadowId})`} stroke="none" />
        </Box>
      </Box>
      {showText && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            flex: "0 0 auto",
            alignSelf: "stretch",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: Math.min(12, Math.max(8, w / 9)),
            fontWeight: 700,
            lineHeight: 1.05,
            color: "#263238",
            px: "2px",
            maxWidth: "40%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
          title={value ?? undefined}
        >
          {value ?? "—"}
        </Typography>
      )}
    </Box>
  );
};
